import { useEffect, useState, type MutableRefObject, type RefObject } from "react";

import { getTopmostVisibleId, lastMessageId, updateIntersectingMap } from "./message-scroll-nav-helpers";

interface UseMessageScrollNavArgs {
    messageIds: string[];
    scrollContainerRef: RefObject<HTMLDivElement | null>;
    onScrollTo?: (messageId: string) => void;
    stickyBottomRef?: MutableRefObject<boolean>;
    collapsedKey?: string;
}

/** View-model for {@link MessageScrollNav}: tracks the active (reading-position)
 * message via an IntersectionObserver and exposes the click-to-scroll handler. */
export function useMessageScrollNav({
    messageIds,
    scrollContainerRef,
    onScrollTo,
    stickyBottomRef,
    collapsedKey,
}: UseMessageScrollNavArgs): { activeId: string | null; handleClick: (id: string) => void } {
    const [activeId, setActiveId] = useState<string | null>(lastMessageId(messageIds));

    // VirtualMessageList autoscrolls to the bottom on mount/reload, so the active
    // dot starts on the last message. Without this reset the dot can stick on a
    // stale value (e.g. the previous chat's first message) until the first
    // IntersectionObserver tick recomputes the topmost-visible one.
    useEffect(() => {
        if (messageIds.length === 0) return;
        setActiveId(lastMessageId(messageIds));
    }, [messageIds]);

    useEffect(() => {
        const root = scrollContainerRef.current;
        if (!root || messageIds.length === 0) return;

        // Fresh per tick: only anchors currently intersecting the viewport, no
        // stale state carried across chat switches.
        const intersecting = new Map<string, IntersectionObserverEntry>();

        const observer = new IntersectionObserver(
            (entries) => {
                updateIntersectingMap(entries, intersecting);
                // Sticky-bottom: keep the active dot pinned to the last message so
                // the observer doesn't promote a higher visible bubble when several
                // trailing bubbles share the viewport (reload / just-sent message).
                if (stickyBottomRef?.current) {
                    setActiveId(lastMessageId(messageIds));
                    return;
                }
                const topmost = getTopmostVisibleId(intersecting);
                if (topmost) setActiveId(topmost);
            },
            {
                root,
                rootMargin: "-20% 0px -20% 0px",
                threshold: [0, 0.25, 0.5, 0.75, 1],
            },
        );

        for (const id of messageIds) {
            const el = root.querySelector<HTMLElement>(`[data-msg-anchor="${id}"]`);
            if (el) observer.observe(el);
        }

        return () => {
            observer.disconnect();
            intersecting.clear();
        };
    }, [messageIds, scrollContainerRef, stickyBottomRef, collapsedKey]);

    const handleClick = (id: string) => {
        setActiveId(id);
        if (onScrollTo) {
            onScrollTo(id);
            return;
        }
        const root = scrollContainerRef.current;
        if (!root) return;
        const target = root.querySelector<HTMLElement>(`[data-msg-anchor="${id}"]`);
        if (!target) return;
        target.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    return { activeId, handleClick };
}
